/**
 * SharePoint Q&A Tool
 * Chat interface with conversation history for RAG-based Q&A over SharePoint content.
 * Mirrors the CAD Analysis UI: sidebar of saved conversations + chat panel.
 * Conversations stored in localStorage. Customer dropdown scopes vector search.
 *
 * MSAL SAFETY: useSharePointApiClient() calls useMsal() internally.
 * SharePointQAProd is ONLY rendered when useDevBypass === false,
 * i.e. when MsalProvider is present. DevPlaceholder is rendered otherwise.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Textarea,
  Spinner,
  Tab,
  TabList,
  TabValue,
  SelectTabEvent,
  SelectTabData,
  Badge,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Select,
} from "@fluentui/react-components";
import {
  Search24Regular,
  Send24Regular,
  Add24Regular,
  ArrowSync24Regular,
  Chat24Regular,
  DocumentTable24Regular,
  MoreHorizontal24Regular,
  Delete24Regular,
} from "@fluentui/react-icons";

import { useSharePointApiClient, SharePointApiError } from "../../hooks/useSharePointApiClient";
import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";
import useUsageTracking from "../../hooks/useUsageTracking";

// ============================================================================
// Types
// ============================================================================

interface SourceRef {
  name: string;
  url?: string | null;
  source_type?: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  sources?: SourceRef[];
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
  customer?: string; // customer scope selected when the conversation was started
}

interface AskResponse {
  question: string;
  answer: string;
  sources?: SourceRef[];
}

interface SyncStatus {
  is_running: boolean;
  last_sync: string | null;
  chunk_count: number;
}

// ============================================================================
// localStorage helpers
// ============================================================================

const STORAGE_KEY = "sharepoint-qa-conversations";
const CUSTOMERS_CACHE_KEY = "sharepoint-qa-customers";

function loadConversations(): Conversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Conversation[];
  } catch {
    return [];
  }
}

function saveConversations(list: Conversation[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Silently ignore storage errors
  }
}

function loadCachedCustomers(): string[] {
  try {
    const raw = localStorage.getItem(CUSTOMERS_CACHE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

function saveCachedCustomers(customers: string[]): void {
  try {
    localStorage.setItem(CUSTOMERS_CACHE_KEY, JSON.stringify(customers));
  } catch {}
}

// ============================================================================
// Retry helper — retries a request with exponential backoff, honoring Retry-After
// ============================================================================

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms),
    ),
  ]);
}

async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  delays: number[] = [2000, 5000, 10000],
  timeoutMs: number = 10000,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await withTimeout(fn(), timeoutMs);
    } catch (err) {
      lastError = err;
      if (attempt >= delays.length) break;

      // Honor Retry-After header on 503, otherwise use fixed backoff
      let delay = delays[attempt];
      if (err instanceof SharePointApiError && err.status === 503 && err.retryAfter) {
        delay = err.retryAfter * 1000;
      }

      console.warn(
        `[SharePointQA] Attempt ${attempt + 1} failed, retrying in ${delay}ms:`,
        err instanceof Error ? err.message : err,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: "6px",
    color: tokens.colorNeutralForegroundOnBrand,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  tabList: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  // Sidebar
  sidebar: {
    width: "280px",
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
  },
  sidebarHeader: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalS,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  conversationList: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingVerticalS,
  },
  conversationItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: "6px",
    cursor: "pointer",
    marginBottom: "4px",
    transition: "background-color 0.1s",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  conversationItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    ":hover": {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  conversationInfo: {
    flex: 1,
    overflow: "hidden",
  },
  conversationCustomer: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    boxSizing: "border-box",
    fontSize: "10px",
    fontWeight: 600,
    lineHeight: "14px",
    color: tokens.colorBrandForeground2,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: "4px",
    padding: "1px 6px",
    marginBottom: "3px",
  },
  conversationTitle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 500,
  },
  conversationPreview: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
  },
  // Main chat area
  mainChat: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  message: {
    marginBottom: tokens.spacingVerticalM,
    maxWidth: "800px",
  },
  messageUser: {
    marginLeft: "auto",
  },
  messageLabel: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "4px",
  },
  messageBubble: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: "12px",
    fontSize: "14px",
    lineHeight: "1.5",
  },
  messageBubbleUser: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderBottomRightRadius: "4px",
    whiteSpace: "pre-wrap",
  },
  messageBubbleAssistant: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomLeftRadius: "4px",
    "& p": { margin: "0 0 8px 0" },
    "& p:last-child": { margin: 0 },
    "& h1, & h2, & h3, & h4": { margin: "12px 0 4px 0", fontWeight: 600 },
    "& ul, & ol": { paddingLeft: "20px", margin: "4px 0 8px 0" },
    "& li": { margin: "2px 0" },
    "& code": {
      fontFamily: "monospace",
      backgroundColor: tokens.colorNeutralBackground3,
      padding: "1px 4px",
      borderRadius: "3px",
      fontSize: "12px",
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: "8px 12px",
      borderRadius: "4px",
      overflowX: "auto",
      fontSize: "12px",
    },
    "& strong": { fontWeight: 600 },
    "& hr": {
      border: "none",
      borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
      margin: "8px 0",
    },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      margin: "8px 0 12px 0",
      fontSize: "13px",
    },
    "& th": {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: 600,
      padding: "6px 12px",
      textAlign: "left",
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    "& td": {
      padding: "5px 12px",
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      verticalAlign: "top",
    },
    "& tr:nth-child(even) td": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  inputContainer: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  inputRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  inputField: {
    flex: 1,
  },
  customerRow: {
    marginBottom: tokens.spacingVerticalXS,
  },
  // Source links rendered under an assistant answer
  sourcesRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: "11px",
    maxWidth: "800px",
  },
  sourcesLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
  },
  sourceLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: "none",
    ":hover": { textDecoration: "underline" },
  },
  sourcePlain: {
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

// ============================================================================
// Progress stages — shown while a question is in flight. Timings are calibrated
// to measured phase durations (embed + search ~1s, doc fetch ~2-5s, LLM
// synthesis the long tail); this is an indicative progress cue, not a live
// readout of the backend phase.
// ============================================================================

const PROGRESS_STAGES: { at: number; label: string }[] = [
  { at: 0, label: "Analyzing your question..." },
  { at: 900, label: "Searching the knowledge base..." },
  { at: 2800, label: "Reading the source documents..." },
  { at: 6500, label: "Writing the answer..." },
];

// ============================================================================
// Main Component
// ============================================================================

export const SharePointQA: React.FC = () => {
  const styles = useStyles();
  const api = useSharePointApiClient();
  const { trackSubtoolAccess } = useUsageTracking();

  // Tab state (single tab, kept for TabList symmetry with CAD Analysis)
  const [selectedTab, setSelectedTab] = useState<TabValue>("assistant");

  // Conversation state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);

  // Customer context — explicit dropdown selection, augments vector search query
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [cachedCustomers] = useState(loadCachedCustomers);
  const [knownCustomers, setKnownCustomers] = useState<string[]>(cachedCustomers);
  const [customersLoading, setCustomersLoading] = useState(cachedCustomers.length === 0);

  // Input / send state
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [progressLabel, setProgressLabel] = useState(PROGRESS_STAGES[0].label);

  // Rename state

  // Sync status
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wasSendingRef = useRef(false);

  // ── Load on mount ──────────────────────────────────────────────────────────

  useEffect(() => {
    setConversations(loadConversations());

    let customersPollId: number | undefined;
    let customersPollStopId: number | undefined;

    fetchWithRetry(() => api.get<SyncStatus>("/api/sync/status"), [3000, 6000, 12000, 20000], 60_000)
      .then(setSyncStatus)
      .catch((err) =>
        console.warn("[SharePointQA] Failed to load sync status:", err instanceof Error ? err.message : err),
      );

    const loadCustomersOnce = () =>
      api.get<{ customers: string[]; storeInitializing?: boolean }>("/api/customers");

    fetchWithRetry(() => loadCustomersOnce(), [3000, 6000, 12000, 20000], 60_000)
      .then((data) => {
        setKnownCustomers(data.customers);
        saveCachedCustomers(data.customers);
        if (data.storeInitializing) {
          customersPollId = window.setInterval(async () => {
            try {
              const d = await loadCustomersOnce();
              setKnownCustomers(d.customers);
              saveCachedCustomers(d.customers);
              if (!d.storeInitializing && customersPollId !== undefined) {
                window.clearInterval(customersPollId);
                customersPollId = undefined;
              }
            } catch {
              /* keep polling until store reports ready */
            }
          }, 4000);
          customersPollStopId = window.setTimeout(() => {
            if (customersPollId !== undefined) window.clearInterval(customersPollId);
          }, 5 * 60_000);
        }
      })
      .catch((err) =>
        console.warn("[SharePointQA] Failed to load customers:", err instanceof Error ? err.message : err),
      )
      .finally(() => setCustomersLoading(false));

    trackSubtoolAccess("sharepoint-agent", "assistant");

    return () => {
      if (customersPollId !== undefined) window.clearInterval(customersPollId);
      if (customersPollStopId !== undefined) window.clearTimeout(customersPollStopId);
    };
  }, []); // intentionally run once on mount

  // Auto-scroll on new messages or when sending
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentConversation?.messages, isSending]);

  // While a question is in flight, advance the progress label on a timer
  // calibrated to measured phase durations. Timers are cleared as soon as
  // sending ends (answer returned or errored).
  useEffect(() => {
    if (!isSending) return;
    setProgressLabel(PROGRESS_STAGES[0].label);
    const timers = PROGRESS_STAGES.slice(1).map((stage) =>
      window.setTimeout(() => setProgressLabel(stage.label), stage.at),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [isSending]);

  // Return keyboard focus to the input box each time a request finishes
  // (success or error) so the user can immediately type the next question.
  // Runs after the textarea re-enables (disabled={isSending} flips false).
  useEffect(() => {
    if (wasSendingRef.current && !isSending) {
      inputRef.current?.focus();
    }
    wasSendingRef.current = isSending;
  }, [isSending]);

  // ── Conversation helpers ───────────────────────────────────────────────────

  const persistConversations = useCallback((list: Conversation[]) => {
    setConversations(list);
    saveConversations(list);
  }, []);

  const createNewConversation = useCallback(() => {
    setCurrentConversation(null);
    setInputValue("");
    setErrorMessage(null);
  }, []);

  const selectConversation = useCallback(
    (id: string) => {
      const found = conversations.find((c) => c.id === id) ?? null;
      setCurrentConversation(found);
      setErrorMessage(null);
    },
    [conversations]
  );

  const deleteConversation = useCallback(
    (id: string) => {
      const updated = conversations.filter((c) => c.id !== id);
      persistConversations(updated);
      if (currentConversation?.id === id) {
        setCurrentConversation(null);
      }
    },
    [conversations, currentConversation, persistConversations]
  );

  // ── Send message ───────────────────────────────────────────────────────────

  const handleSend = useCallback(async (textOverride?: string) => {
    const text = (textOverride !== undefined ? textOverride : inputValue).trim();
    if (!text || isSending) return;

    const now = new Date().toISOString();
    const userMsg: Message = { role: "user", content: text, timestamp: now };

    // Determine conversation (create new if needed)
    let conv: Conversation;
    if (currentConversation) {
      conv = {
        ...currentConversation,
        messages: [...currentConversation.messages, userMsg],
        updated_at: now,
      };
    } else {
      conv = {
        id: crypto.randomUUID(),
        title: text.slice(0, 60),
        messages: [userMsg],
        created_at: now,
        updated_at: now,
        customer: selectedCustomer || undefined,
      };
    }

    setCurrentConversation(conv);
    setInputValue("");
    setIsSending(true);
    setErrorMessage(null);

    try {
      // Send prior messages as history so the LLM can resolve follow-up
      // references ("they", "that customer", "it", etc.) from context.
      // conv.messages already has the new user message appended, so we exclude
      // the last entry (index -1) and cap at 10 prior turns.
      const history = conv.messages
        .slice(0, -1)
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await fetchWithRetry(
        () =>
          api.post<AskResponse>("/api/ask", {
            question: text,
            history,
            ...(selectedCustomer ? { customer: selectedCustomer } : {}),
          }),
        [2000, 5000, 10000, 15000],
        60_000,
      );

      const assistantMsg: Message = {
        role: "assistant",
        content: response.answer,
        timestamp: new Date().toISOString(),
        sources: response.sources,
      };

      const finalConv: Conversation = {
        ...conv,
        messages: [...conv.messages, assistantMsg],
        updated_at: new Date().toISOString(),
      };

      setCurrentConversation(finalConv);

      // Upsert into conversations list
      const exists = conversations.some((c) => c.id === finalConv.id);
      const updated = exists
        ? conversations.map((c) => (c.id === finalConv.id ? finalConv : c))
        : [finalConv, ...conversations];
      persistConversations(updated);
    } catch (err: any) {
      const status = err?.status;
      if (status === 429) {
        setErrorMessage("The AI service is busy — please wait a moment and try again.");
      } else if (status === 503) {
        setErrorMessage(
          "Search index is still starting up. Wait a few seconds and try again, or refresh the page.",
        );
      } else {
        setErrorMessage(err?.message || "Failed to get answer. Please try again.");
      }
      // Roll back the pending user message and restore input so the user can retry
      const rolledBack = { ...conv, messages: conv.messages.slice(0, -1) };
      setCurrentConversation(rolledBack.messages.length > 0 ? rolledBack : null);
      setInputValue(text);
    } finally {
      setIsSending(false);
    }
  }, [api, inputValue, isSending, currentConversation, conversations, persistConversations, selectedCustomer]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Sync ───────────────────────────────────────────────────────────────────

  const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      await api.post("/api/sync");
      setTimeout(async () => {
        try {
          const status = await api.get<SyncStatus>("/api/sync/status");
          setSyncStatus(status);
        } catch {}
        setIsSyncing(false);
      }, 2000);
    } catch (err: any) {
      setErrorMessage(err?.message || "Failed to trigger sync.");
      setIsSyncing(false);
    }
  };

  // ── Tab select ─────────────────────────────────────────────────────────────

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value);
    trackSubtoolAccess("sharepoint-agent", String(data.value));
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <div className={styles.headerIcon}>
            <Search24Regular />
          </div>
          <div>
            <Text size={500} weight="semibold">
              SharePoint Q&amp;A
            </Text>
            <Text size={200} style={{ display: "block", color: tokens.colorNeutralForeground3 }}>
              AI-powered search across your SharePoint knowledge base
            </Text>
          </div>
        </div>
        <div className={styles.headerActions}>
          {syncStatus && syncStatus.chunk_count >= 0 && (
            <Badge appearance="filled" color="informative" size="small">
              {syncStatus.chunk_count.toLocaleString()} chunks
            </Badge>
          )}
          <Button
            appearance="subtle"
            icon={isSyncing ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
            onClick={handleSync}
            disabled={isSyncing}
            title="Trigger incremental sync from SharePoint"
          >
            Sync
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabList}>
        <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
          <Tab value="assistant" icon={<Chat24Regular />}>
            AI Assistant
          </Tab>
        </TabList>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Text weight="semibold">Conversations</Text>
            <Button
              appearance="subtle"
              icon={<Add24Regular />}
              onClick={createNewConversation}
              title="New conversation"
            />
          </div>

          <div className={styles.conversationList}>
            {conversations.length === 0 ? (
              <Text
                size={200}
                style={{ padding: "16px", color: tokens.colorNeutralForeground3, display: "block" }}
              >
                No conversations yet. Ask a question to start one!
              </Text>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`${styles.conversationItem} ${
                    currentConversation?.id === conv.id ? styles.conversationItemActive : ""
                  }`}
                  onClick={() => selectConversation(conv.id)}
                >
                  <div className={styles.conversationInfo}>
                    {conv.customer && (
                      <div className={styles.conversationCustomer} title={conv.customer}>
                        {conv.customer}
                      </div>
                    )}
                    <div className={styles.conversationTitle}>{conv.title}</div>
                    {conv.messages.length > 0 && (
                      <div className={styles.conversationPreview}>
                        {conv.messages[conv.messages.length - 1].content.slice(0, 60)}
                      </div>
                    )}
                  </div>

                  <Menu>
                    <MenuTrigger>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<MoreHorizontal24Regular />}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<Delete24Regular />}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conv.id);
                          }}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main chat */}
        <div className={styles.mainChat}>
          {currentConversation ? (
            <div className={styles.messagesContainer}>
              {currentConversation.messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`${styles.message} ${
                    msg.role === "user" ? styles.messageUser : ""
                  }`}
                >
                  <div className={styles.messageLabel}>
                    {msg.role === "user" ? "You" : "SharePoint Assistant"}
                  </div>
                  <div
                    className={`${styles.messageBubble} ${
                      msg.role === "user"
                        ? styles.messageBubbleUser
                        : styles.messageBubbleAssistant
                    }`}
                  >
                    {msg.role === "user" ? (
                      msg.content
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    )}
                  </div>
                  {msg.role === "assistant" &&
                    msg.sources &&
                    msg.sources.length > 0 && (
                      <div className={styles.sourcesRow}>
                        <span className={styles.sourcesLabel}>Sources:</span>
                        {msg.sources.map((s, i) =>
                          s.url ? (
                            <a
                              key={i}
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.sourceLink}
                              title={s.name}
                            >
                              {s.name}
                            </a>
                          ) : (
                            <span
                              key={i}
                              className={styles.sourcePlain}
                              title={s.name}
                            >
                              {s.name}
                            </span>
                          ),
                        )}
                      </div>
                    )}
                </div>
              ))}

              {isSending && (
                <div className={styles.message}>
                  <div className={styles.messageLabel}>SharePoint Assistant</div>
                  <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Spinner size="tiny" />
                      <span>{progressLabel}</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className={styles.emptyState}>
              <DocumentTable24Regular style={{ fontSize: 48, marginBottom: 16 }} />
              <Text size={500} weight="semibold">
                SharePoint Assistant
              </Text>
              <Text size={300} style={{ marginTop: 8, maxWidth: 400, textAlign: "center" }}>
                Ask questions about your SharePoint content and get AI-powered answers synthesized
                from your documents. Use the customer dropdown to focus the search.
              </Text>
              {syncStatus?.last_sync && (
                <Text
                  size={200}
                  style={{ marginTop: 16, color: tokens.colorNeutralForeground3 }}
                >
                  Last synced: {new Date(syncStatus.last_sync).toLocaleString()}
                </Text>
              )}
            </div>
          )}

          {errorMessage && (
            <MessageBar intent="error" style={{ margin: "0 16px 8px 16px" }}>
              <MessageBarBody>
                <MessageBarTitle>Error</MessageBarTitle>
                {errorMessage}
              </MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.inputContainer}>
            <div className={styles.customerRow}>
              <Select
                size="small"
                value={selectedCustomer}
                onChange={(_, data) => setSelectedCustomer(data.value)}
                disabled={customersLoading}
              >
                <option value="">{customersLoading ? "Loading customers..." : "All Customers"}</option>
                {knownCustomers.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
            </div>
            <div className={styles.inputRow}>
              <Textarea
                ref={inputRef}
                className={styles.inputField}
                placeholder="Ask a question about your SharePoint content..."
                value={inputValue}
                onChange={(_, data) => setInputValue(data.value)}
                onKeyDown={handleKeyDown}
                disabled={isSending}
                rows={2}
                resize="vertical"
              />
              <VoiceTextInputButton
                value={inputValue}
                onChange={setInputValue}
                onDictationComplete={(t) => void handleSend(t)}
                disabled={isSending || customersLoading}
              />
              <Button
                appearance="primary"
                icon={isSending ? <Spinner size="tiny" /> : <Send24Regular />}
                onClick={() => void handleSend()}
                disabled={!inputValue.trim() || isSending || customersLoading}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePointQA;
